import { readonly, ref } from 'vue'

const STORAGE_KEY = 'ggi-maps:theme'

type Theme = 'light' | 'dark'

const theme = ref<Theme>('light')

/**
 * Reads the stored preference.
 *
 * Every access is wrapped: localStorage throws in a private window, when
 * site data is blocked, or under some corporate policies. A theme preference
 * is never worth breaking the app for, so any failure falls back to light.
 */
function readStoredTheme(): Theme | null {
  try {
    const stored = localStorage.getItem(STORAGE_KEY)
    return stored === 'dark' || stored === 'light' ? stored : null
  } catch {
    return null
  }
}

function persistTheme(value: Theme): void {
  try {
    localStorage.setItem(STORAGE_KEY, value)
  } catch {
    // The preference simply does not survive a reload. Not worth reporting.
  }
}

function applyTheme(value: Theme): void {
  const root = document.documentElement

  // Two switches for one theme, on purpose: the Gigante Analytics design
  // system reads `data-theme`, Element Plus reads a `.dark` class. Setting
  // both keeps our tokens and the component library on the same theme.
  root.dataset.theme = value
  root.classList.toggle('dark', value === 'dark')
}

/**
 * Light/dark theme, shared across every component that calls this.
 *
 * The choice is the user's, not the operating system's: the tool is used in
 * the field under changing light, and the phone's automatic switch is a poor
 * proxy for whether a floor plan is readable right now. The OS preference is
 * only the starting point when nothing was chosen before.
 */
export function useTheme() {
  function initTheme(): void {
    const stored = readStoredTheme()

    if (stored) {
      theme.value = stored
    } else {
      const prefersDark =
        typeof window.matchMedia === 'function' &&
        window.matchMedia('(prefers-color-scheme: dark)').matches
      theme.value = prefersDark ? 'dark' : 'light'
    }

    applyTheme(theme.value)
  }

  function toggleTheme(): void {
    theme.value = theme.value === 'dark' ? 'light' : 'dark'
    applyTheme(theme.value)
    persistTheme(theme.value)
  }

  return {
    theme: readonly(theme),
    initTheme,
    toggleTheme,
  }
}
